import { Box, Flex } from '@chakra-ui/react';
import { useCallback, useLayoutEffect, useRef } from 'react';

import {
  formatTransform,
  INDICATOR_BASE_WIDTH,
  TAB_HIT_PADDING_X,
} from '../../CustomDash/components/tab-indicator';

// Four primary view-tabs from the design (Value Added rides as a toggle on
// Output in a later phase, not as a tab). Only Output is implemented; the
// rest render placeholders.
export const VIEW_TABS = [
  'Output',
  'Employment',
  'Household Income',
  'Tax',
] as const;
export type ViewTab = (typeof VIEW_TABS)[number];

interface ViewTabStripProps {
  active: ViewTab;
  onSelect: (tab: ViewTab) => void;
}

// Page-level view tabs styled to match the dashboard's new-layout tab bar
// (AnalyticsViewTabs): a sliding green underline with 14px/600 labels that go
// from muted to primary. Driven by local state since these switch result views
// rather than routes, but the indicator animation reuses the shared
// tab-indicator helpers so it stays visually consistent.
export function ViewTabStrip({ active, onSelect }: ViewTabStripProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const activeIndex = VIEW_TABS.indexOf(active);

  const slideIndicatorTo = useCallback((tab: HTMLButtonElement | null) => {
    const el = indicatorRef.current;
    if (!tab || !el) return;
    el.style.opacity = '1';
    el.style.transform = formatTransform({
      left: tab.offsetLeft + TAB_HIT_PADDING_X,
      width: tab.offsetWidth - TAB_HIT_PADDING_X * 2,
    });
  }, []);

  useLayoutEffect(() => {
    // rAF lets the browser paint the previous position once so the underline
    // animates to the new tab instead of jumping.
    const raf = requestAnimationFrame(() =>
      slideIndicatorTo(tabRefs.current[activeIndex] ?? null)
    );
    return () => cancelAnimationFrame(raf);
  }, [activeIndex, slideIndicatorTo]);

  return (
    <Flex
      as="nav"
      aria-label="Result views"
      data-testid="results-tabs"
      alignItems="stretch"
      gap={1}
      height="32px"
      position="relative"
    >
      {VIEW_TABS.map((tab, i) => {
        const isActive = tab === active;
        return (
          <Box
            as="button"
            type="button"
            key={tab}
            ref={(el: HTMLButtonElement | null) => {
              tabRefs.current[i] = el;
            }}
            onClick={() => onSelect(tab)}
            display="flex"
            alignItems="center"
            height="48px"
            my="-8px"
            px={`${TAB_HIT_PADDING_X}px`}
            fontSize="14px"
            fontWeight="600"
            color={isActive ? 'text.primary' : '#8F9CB2'}
            whiteSpace="nowrap"
            _hover={{ color: 'text.primary' }}
            data-testid={`tab-${tab.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {tab}
          </Box>
        );
      })}
      <div
        ref={indicatorRef}
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '-1px',
          left: 0,
          width: `${INDICATOR_BASE_WIDTH}px`,
          height: '2px',
          background: 'var(--chakra-colors-green-500)',
          transform: 'translate3d(0, 0, 0) scaleX(0)',
          transformOrigin: 'left center',
          transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'transform',
          pointerEvents: 'none',
          opacity: 0,
        }}
      />
    </Flex>
  );
}
